import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, output } from '@angular/core';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { DialPlanExceptionResource } from '../../../resources/dial-plan-exception.resource';
import { DialPlanExceptionGroup, DnDidRange, DnExceptionRange } from '../../../../shared/models/generated/ziroModels';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { ZiroExtensionRangesComponentConfig } from '../../../../forms/fields/extension-ranges/ziro-extension-range-models';
import { ZiroTextareaConfig } from '../../../../forms/fields/textarea/ziro-textarea.component';
import { HtmlCheckboxType, ZiroCheckboxConfig } from '../../../../forms/fields/checkbox/ziro-checkbox.component';
import { ZiroExtensionRangesComponent } from '../../../../forms/fields/extension-ranges/ziro-extension-ranges.component';
import { uniq } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { ModalBodyClass } from '../../../../modals/modal-body';
import { DetailedModalComponent } from '../../../../modals/detailed-modal/detailed-modal.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';

const GROUP_NAME_MAX_LENGTH = 255;

export interface DialPlanExceptionForm {
  id: string;
  groupName: string;
  dnRanges: DnExceptionRange[];
  exceptionList: string;
  legalHold: boolean;
}

@Component({
  selector: 'ziro-dn-exception-edit',
  templateUrl: './dial-plan-exception-management-edit.component.html',
  standalone: false,
})
export class DialPlanExceptionManagementEditComponent extends ModalBodyClass implements OnInit, OnDestroy {
  private dialPlanExceptionResource = inject(DialPlanExceptionResource);
  protected _ziroFormStateService: ZiroFormStateService;
  private modalComponent = inject<DetailedModalComponent<any>>(DetailedModalComponent);

  allGroups: DialPlanExceptionGroup[];
  initialDnExceptionGroup = {} as DialPlanExceptionGroup;
  readonly closeTrigger = output<DialPlanExceptionGroup>();

  @ViewChild(ZiroExtensionRangesComponent) childComponent: ZiroExtensionRangesComponent;
  @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;

  isSubmitted: boolean;
  allGroupNames: string[];
  ranges: DnExceptionRange[];
  isChildFormValid = true;
  formConfig: ZiroFormConfig;
  ziroIcons = ZiroIcons;

  private _subscription = new Subscription();

  private static _unpackNumberRangeUpdate(formData: DialPlanExceptionForm): DnDidRange[] {
    return formData.dnRanges ? formData.dnRanges : [];
  }

  ngOnInit(): void {
    this.formConfig = {
      fields: {
        groupName: {
          label: 'tkey;dialplanmanagement.admin.group.name',
          dataAutomation: 'dial-plan-exception-group-name',
          required: true,
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          validation: [
            {
              validator: (val: string) =>
                val && !this.allGroupNames.includes(val)
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID,
              message: 'tkey;dialplanmanagement.admin.group.name.alreadyInUse',
            },
            {
              validator: (val: string) =>
                val.length <= GROUP_NAME_MAX_LENGTH ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: {
                content: 'tkey;validators.global.error.maxlength',
                params: {
                  maxlength: GROUP_NAME_MAX_LENGTH,
                },
              },
            },
          ],
        },
        dnRanges: {
          label: 'tkey;dialplanmanagement.admin.group.dnranges',
          dataAutomation: 'dial-plan-exception-group-ranges',
          componentConfig: new ZiroExtensionRangesComponentConfig({
            optionalValidators: {
              overlappingRanges: true,
            },
          }),
        },
        exceptionList: {
          label: 'tkey;dnexception.admin.group.dnlist',
          dataAutomation: 'dial-plan-exception-list',
          componentConfig: new ZiroTextareaConfig(),
          validation: [
            {
              validator: (val: string) => {
                return !val ||
                  val
                    .split(',')
                    .filter((dn) => !!dn.trim())
                    .every((dn) => !!dn.match(/^\s*\+?[0-9]*\s*$/))
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID;
              },
              message: 'tkey;dnexception.admin.group.dnlistformat',
            },
          ],
        },
        legalHold: {
          label: 'tkey;dnexception.admin.group.legal_hold',
          labelToolTipText: 'tkey;dnexception.admin.group.legal_hold.tooltip',
          dataAutomation: 'forbid-modifications',
          componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.LEFT_ALIGNED_CHECKBOX }),
          labelToolTipIconClass: this.ziroIcons.TOOLTIP,
        },
      },
    } as ZiroFormConfig;

    this.allGroupNames = this.modalComponent.modalViewProperties.allGroups.map(
      (dng: DialPlanExceptionGroup) => dng.name,
    );
    this.entitySource.next({
      ...this.modalComponent.modalViewProperties.dnExceptionGroup,
    });

    const validateAndSubmitSourceSub = this._validateAndSubmitSource.subscribe(() => {
      this.isSubmitted = true;
    });
    this._subscription.add(validateAndSubmitSourceSub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  isAlertHidden(): boolean {
    this.isChildFormValid = this.childComponent?.childFormComponent?.isFormValid();
    return !this.isSubmitted || (this.isFormValid() && this.isChildFormValid);
  }

  toFormData = (entity: DialPlanExceptionGroup): DialPlanExceptionForm => {
    return {
      id: entity.id,
      groupName: entity.name,
      dnRanges: entity.exceptionRanges,
      exceptionList: entity.exceptionValues ? entity.exceptionValues.join(', ') : '',
      legalHold: entity.legalHold,
    } as DialPlanExceptionForm;
  };

  toEntity = (formData: DialPlanExceptionForm): DialPlanExceptionGroup => {
    return {
      id: formData.id?.toString() || null,
      name: formData.groupName || '',
      exceptionRanges: DialPlanExceptionManagementEditComponent._unpackNumberRangeUpdate(formData),
      exceptionValues: formData.exceptionList
        ? formData.exceptionList
            .split(',')
            .filter((dn) => !dn.trim().match(/^\+?$/))
            .map((dn) => dn.trim())
        : [],
      legalHold: formData.legalHold,
    } as DialPlanExceptionGroup;
  };

  submit(): Observable<any> {
    const modalObs$ = new Observable<any>((subscriber) => {
      if (!this.childComponent.childFormComponent.isFormValid()) {
        this.isSubmitted = true;
        this._validateAndSubmitSource.next(false);
      } else {
        const reqBody = {
          ...this.entity,
          exceptionValues: uniq(this.entity.exceptionValues),
        };

        (!this.entity.id
          ? this.dialPlanExceptionResource.post(reqBody)
          : this.dialPlanExceptionResource.put(this.entity.id, reqBody)
        ).subscribe(() => {
          subscriber.next(reqBody);
          subscriber.complete();
        });
      }
    });
    return modalObs$.pipe();
  }
}
